import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sheet, Link2, Unlink, RefreshCw, ExternalLink, Check, Loader2, Database, Users, Activity, Bell } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function GoogleSheetsSettings() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState('');

  useEffect(() => {
    loadStatus();
    
    // Check for successful connection
    if (searchParams.get('sheets') === 'connected') {
      toast.success('Google Sheets connected successfully!');
      loadStatus();
    }
  }, [searchParams]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/oauth/sheets/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load status:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectSheets = () => {
    // Pass token as query parameter for the OAuth redirect flow
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      toast.error('Please log in first');
      return;
    }
    window.location.href = `${API}/oauth/sheets/login?token=${encodeURIComponent(authToken)}`;
  };

  const disconnectSheets = async () => {
    try {
      await axios.delete(`${API}/oauth/sheets/disconnect`);
      toast.success('Google Sheets disconnected');
      setStatus({ ...status, connected: false, spreadsheet_id: null, spreadsheet_url: null });
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const createSpreadsheet = async () => {
    try {
      setCreating(true);
      const response = await axios.post(`${API}/sheets/create?title=BE ELITE APP Data`);
      toast.success('Spreadsheet created!');
      setStatus({
        ...status,
        spreadsheet_id: response.data.spreadsheet_id,
        spreadsheet_url: response.data.url
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create spreadsheet');
    } finally {
      setCreating(false);
    }
  };

  const linkSpreadsheet = async () => {
    if (!spreadsheetId.trim()) {
      toast.error('Please enter a spreadsheet ID');
      return;
    }
    
    try {
      setLinking(true);
      const response = await axios.post(`${API}/sheets/link?spreadsheet_id=${spreadsheetId}`);
      toast.success('Spreadsheet linked!');
      setStatus({
        ...status,
        spreadsheet_id: response.data.spreadsheet_id,
        spreadsheet_url: response.data.url
      });
      setSpreadsheetId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link spreadsheet');
    } finally {
      setLinking(false);
    }
  };

  const syncAllData = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/sheets/sync/all`);
      toast.success('All data synced to Google Sheets!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <Sheet className="h-8 w-8 text-green-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white uppercase tracking-tight">Google Sheets Integration</h1>
              <p className="text-zinc-500 mt-1">Sync athlete data to Google Sheets in real-time</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        {/* Connection Status */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Connection Status</h2>
          
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-3 h-3 rounded-full ${status.connected ? 'bg-green-500' : 'bg-zinc-600'}`} />
            <span className={`text-lg font-medium ${status.connected ? 'text-green-400' : 'text-zinc-400'}`}>
              {status.connected ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          
          {!status.configured ? (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
              <p className="text-yellow-400 text-sm">
                Google Sheets is not configured. Please contact support to set up Google OAuth credentials.
              </p>
            </div>
          ) : !status.connected ? (
            <Button
              onClick={connectSheets}
              className="bg-[#02b5ff] hover:bg-[#0099dd] text-white"
              data-testid="connect-sheets-btn"
            >
              <Link2 className="mr-2 h-4 w-4" />
              Connect Google Sheets
            </Button>
          ) : (
            <div className="space-y-4">
              {status.spreadsheet_url && (
                <div className="flex items-center gap-4">
                  <a
                    href={status.spreadsheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#02b5ff] hover:underline flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Spreadsheet
                  </a>
                </div>
              )}
              <Button
                onClick={disconnectSheets}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                data-testid="disconnect-sheets-btn"
              >
                <Unlink className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </div>
          )}
        </div>

        {/* Spreadsheet Management */}
        {status.connected && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Spreadsheet Management</h2>
            
            {!status.spreadsheet_id ? (
              <div className="space-y-6">
                {/* Create New */}
                <div>
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Create New Spreadsheet</h3>
                  <Button
                    onClick={createSpreadsheet}
                    disabled={creating}
                    className="bg-green-600 hover:bg-green-500 text-white"
                    data-testid="create-spreadsheet-btn"
                  >
                    {creating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sheet className="mr-2 h-4 w-4" />
                    )}
                    Create "BE ELITE APP Data"
                  </Button>
                  <p className="text-xs text-zinc-500 mt-2">Creates a new spreadsheet with all required sheets</p>
                </div>
                
                <div className="border-t border-zinc-800 pt-6">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase mb-3">Or Link Existing Spreadsheet</h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={spreadsheetId}
                      onChange={(e) => setSpreadsheetId(e.target.value)}
                      placeholder="Paste spreadsheet ID here"
                      className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#02b5ff]"
                      data-testid="spreadsheet-id-input"
                    />
                    <Button
                      onClick={linkSpreadsheet}
                      disabled={linking}
                      variant="outline"
                      className="border-zinc-700 text-white hover:bg-zinc-800"
                      data-testid="link-spreadsheet-btn"
                    >
                      {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Find the ID in your spreadsheet URL: docs.google.com/spreadsheets/d/<span className="text-[#02b5ff]">SPREADSHEET_ID</span>/edit
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-green-400">
                  <Check className="h-5 w-5" />
                  <span>Spreadsheet linked</span>
                </div>
                <a
                  href={status.spreadsheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#02b5ff] hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in Google Sheets
                </a>
              </div>
            )}
          </div>
        )}

        {/* Sync Data */}
        {status.connected && status.spreadsheet_id && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Sync Data</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <Users className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Athletes</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <Database className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Measurements</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <Activity className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Performance</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <Bell className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Alerts</p>
              </div>
            </div>
            
            <Button
              onClick={syncAllData}
              disabled={syncing}
              className="w-full h-12 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-bold uppercase tracking-wider"
              data-testid="sync-all-btn"
            >
              {syncing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Syncing All Data...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Sync All Data Now
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 mt-3 text-center">
              New data is automatically synced when athletes, measurements, or bookings are added
            </p>
          </div>
        )}

        {/* Data Sheets Info */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Synced Data Sheets</h2>
          <div className="space-y-3">
            {[
              { name: 'Athletes', desc: 'All athlete profiles with age, gender, coach assignment' },
              { name: 'Measurements', desc: 'Height, weight, BMI, sitting height, arm span' },
              { name: 'Bookings', desc: 'All booking records with service and status' },
              { name: 'Performance Tests', desc: 'Athletic test results with grades' },
              { name: 'Coach Ratings', desc: 'Psychological and social assessments' },
              { name: 'Alerts', desc: 'System alerts and notifications' }
            ].map((sheet, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-zinc-800/30 rounded">
                <Sheet className="h-4 w-4 text-green-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium text-sm">{sheet.name}</p>
                  <p className="text-zinc-500 text-xs">{sheet.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
